import React from 'react';
import { 
  Layers, 
  ArrowLeft,
  Tv,
  Film
} from 'lucide-react';
import { CuratedCollection, MediaItem, WatchHistoryItem } from '../types';
import { MediaCard } from './MediaCard';
import { MediaRow } from './MediaRow';
import { CollectionCard } from './CollectionCard';

interface CollectionsViewProps {
  collections: CuratedCollection[];
  catalog: MediaItem[];
  selectedCollectionId: string | null;
  onSelectCollection: (collectionId: string | null) => void;
  onSelectMedia: (item: MediaItem) => void;
  onPlayMedia: (item: MediaItem, episodeIndex?: number, initialTime?: number) => void;
  historyMap: Record<string, WatchHistoryItem>;
}

export const CollectionsView: React.FC<CollectionsViewProps> = ({
  collections,
  catalog,
  selectedCollectionId,
  onSelectCollection,
  onSelectMedia,
  onPlayMedia,
  historyMap
}) => {
  const selectedCol = selectedCollectionId 
    ? collections.find(c => c.id === selectedCollectionId) 
    : null;

  // Helper to get items for a collection
  const getCollectionItems = (col: CuratedCollection) => {
    return catalog.filter(item => 
      item.collectionId === col.id ||
      item.collectionId === col.slug ||
      col.itemIds?.includes(item.id) ||
      item.collections?.includes(col.id) ||
      item.collections?.includes(col.slug)
    );
  };

  // ==========================================================
  // VIEW A: DETALL DE LA COL·LECCIÓ (Quan una col·lecció està seleccionada)
  // ==========================================================
  if (selectedCol) {
    const colItems = getCollectionItems(selectedCol);
    const seriesItems = colItems.filter(item => item.type === 'series');
    const movieItems = colItems.filter(item => item.type === 'movie');

    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 space-y-8 animate-in fade-in duration-300">
        
        {/* Navigation Breadcrumb */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-6">
          <div className="flex items-start sm:items-center gap-3">
            <button
              id="btn-back-to-collections"
              type="button"
              data-focusable="true"
              onClick={() => onSelectCollection(null)}
              className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-all group flex items-center gap-1.5 text-xs font-semibold cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
              <span>Totes les col·leccions</span>
            </button>

            <div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded text-[11px] font-black uppercase tracking-wider bg-sky-500 text-black">
                  COL·LECCIÓ
                </span>
                <h1 className="font-display font-black text-2xl sm:text-3xl md:text-4xl text-white">
                  {selectedCol.title}
                </h1>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-2xl">
                {selectedCol.description || 'Contingut de la col·lecció.'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            <span className="px-3.5 py-1.5 rounded-xl bg-sky-500/10 border border-sky-500/20 text-xs font-mono font-bold text-sky-400">
              {colItems.length} Títols ({seriesItems.length} Sèries, {movieItems.length} Pel·lícules)
            </span>
          </div>
        </div>

        {/* Content rows or grid */}
        {colItems.length === 0 ? (
          <div className="text-center py-20 bg-[#0c1220] rounded-3xl border border-white/5 p-8 max-w-md mx-auto space-y-3">
            <p className="text-sm font-semibold text-slate-300">
              Aquesta col·lecció no té títols associats actualment.
            </p>
          </div>
        ) : (
          <div className="space-y-10">
            {/* Series Section */}
            {seriesItems.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Tv className="w-5 h-5 text-sky-400" />
                  <h2 className="font-display font-black text-xl text-white">
                    Sèries ({seriesItems.length})
                  </h2>
                </div>
                <div 
                  data-grid="true"
                  data-columns="5"
                  className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 xl:grid-cols-5 gap-4"
                >
                  {seriesItems.map(item => (
                    <MediaCard
                      key={item.id}
                      item={item}
                      className="w-full"
                      onSelect={() => onSelectMedia(item)}
                      onPlay={() => onPlayMedia(item)}
                      historyItem={historyMap[item.id]}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Movies Section */}
            {movieItems.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Film className="w-5 h-5 text-cyan-400" />
                  <h2 className="font-display font-black text-xl text-white">
                    Pel·lícules ({movieItems.length})
                  </h2>
                </div>
                <div 
                  data-grid="true"
                  data-columns="5"
                  className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 xl:grid-cols-5 gap-4"
                >
                  {movieItems.map(item => (
                    <MediaCard
                      key={item.id}
                      item={item}
                      className="w-full"
                      onSelect={() => onSelectMedia(item)}
                      onPlay={() => onPlayMedia(item)}
                      historyItem={historyMap[item.id]}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    );
  }

  // ==========================================================
  // VIEW B: PANTALLA PRINCIPAL DE COL·LECCIONS
  // Un únic pòster per cada col·lecció, identificades pel seu nom
  // ==========================================================
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 space-y-8 animate-in fade-in duration-300">
      
      {/* Header */}
      <div className="border-b border-white/10 pb-6">
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 rounded text-[11px] font-black uppercase tracking-wider bg-sky-500 text-black">
            CATÀLEGS
          </span>
          <h1 className="font-display font-black text-3xl sm:text-4xl text-white">
            Col·leccions
          </h1>
        </div>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">
          Tria una col·lecció per accedir al seu catàleg complet de sèries i pel·lícules.
        </p>
      </div>

      {/* Grid of Collections - ONE single poster card per collection */}
      {collections.length === 0 ? (
        <div className="text-center py-20 bg-[#0c1220] rounded-3xl border border-white/5 p-8 max-w-md mx-auto space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-sky-500/20 text-sky-400 flex items-center justify-center mx-auto">
            <Layers className="w-8 h-8" />
          </div>
          <h3 className="font-display font-bold text-xl text-white">
            No hi ha cap col·lecció disponible
          </h3>
          <p className="text-xs text-slate-400">
            Pots gestionar o pujar noves col·leccions des del desplegable d'opcions del teu usuari.
          </p>
        </div>
      ) : (
        <div 
          data-grid="true"
          data-columns="5"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 xl:grid-cols-5 gap-4 sm:gap-6"
        >
          {collections.map(col => (
            <CollectionCard
              key={col.id}
              collection={col}
              catalog={catalog}
              onClick={() => onSelectCollection(col.id)}
            />
          ))}
        </div>
      )}

    </div>
  );
};
